// src/pages/RuleEditor.jsx
import React, { useState, useEffect, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button, TextField,
  Chip, IconButton, FormControl, InputLabel, Select, MenuItem,
  Paper, Tooltip, Alert, Stack, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon,
  Save as SaveIcon, PlayArrow as PlayIcon, Pause as PauseIcon,
  Settings as SettingsIcon, Psychology as LogicIcon,
  Build as ActionIcon, ContentCopy as CopyIcon
} from '@mui/icons-material';

// Дефолтные наборы (используются, если schema не передана)
const DEFAULT_CONDITION_TYPES = [
  "состояние_персонажа", "окружение", "время", "системное",
  "социальное", "отношения", "инвентарь", "когнитивное_состояние"
];

const DEFAULT_OPERATORS = [
  "равно", "не_равно", "больше", "меньше",
  "больше_или_равно", "меньше_или_равно", "существует", "содержит"
];

const DEFAULT_ACTION_TYPES = [
  "назначить_задачу", "выполнить_действие", "изменить_состояние",
  "отправить_сообщение", "пользовательское", "изменить_инвентарь"
];

const DEFAULT_TARGET_TYPES = ["self", "system", "character", "object", "group"];

// --- Вспомогательные компоненты ---

const SectionTitle = ({ icon, title, action }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, mt: 3 }}>
    <Box sx={{ mr: 1, color: 'primary.main' }}>{icon}</Box>
    <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 'bold' }}>{title}</Typography>
    {action}
  </Box>
);

// --- Строка параметра (с локальным стейтом для ключа) ---
const ParameterRow = ({ paramKey, paramValue, onChange, onDelete, keyEditable = true }) => {
  // Локальный стейт ключа, чтобы переименование не сбивалось на каждом символе
  const [localKey, setLocalKey] = useState(paramKey);

  useEffect(() => {
    setLocalKey(paramKey);
  }, [paramKey]);

  const commitKey = () => {
    const trimmed = (localKey || '').trim();
    if (trimmed === '' || trimmed === paramKey) {
      // откат к исходному
      setLocalKey(paramKey);
      return;
    }
    onChange(paramKey, trimmed, paramValue);
  };

  return (
    <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
      <TextField
        size="small"
        value={localKey}
        onChange={(e) => setLocalKey(e.target.value)}
        onBlur={commitKey}
        onKeyDown={(e) => { if (e.key === 'Enter') commitKey(); }}
        disabled={!keyEditable}
        sx={{ width: 160 }}
        placeholder="Ключ"
      />
      <TextField
        size="small"
        value={paramValue ?? ''}
        onChange={(e) => onChange(paramKey, paramKey, e.target.value)}
        fullWidth
        placeholder="Значение (строка, число, JSON)"
      />
      <IconButton size="small" onClick={() => onDelete(paramKey)} color="error">
        <DeleteIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

// --- Редактор одного Условия ---
const ConditionItem = ({
  condition, index, onChange, onDelete,
  conditionTypes, operators
}) => {
  const updateField = (field, value) => {
    onChange(index, { ...condition, [field]: value });
  };

  const updateParamValue = (oldKey, newKey, value) => {
    const params = { ...(condition.parameters || {}) };
    if (newKey !== oldKey) {
      delete params[oldKey];
    }
    params[newKey] = value;
    updateField('parameters', params);
  };

  const deleteParam = (key) => {
    const params = { ...(condition.parameters || {}) };
    delete params[key];
    updateField('parameters', params);
  };

  const addParam = () => {
    const key = window.prompt('Имя параметра:');
    if (!key) return;
    const trimmed = key.trim();
    if (!trimmed) return;
    const params = { ...(condition.parameters || {}), [trimmed]: '' };
    updateField('parameters', params);
  };

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderLeft: '4px solid #2196F3' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Chip label={`Условие ${index + 1}`} size="small" color="info" variant="outlined" />
        <IconButton size="small" onClick={() => onDelete(index)} color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип</InputLabel>
            <Select
              value={condition.type || ''}
              label="Тип"
              onChange={(e) => updateField('type', e.target.value)}
            >
              {conditionTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth size="small"
            label="Поле (Field)"
            value={condition.field || ''}
            onChange={(e) => updateField('field', e.target.value)}
            placeholder="например: need_type"
          />
        </Grid>
        <Grid item xs={12} sm={4}>
          <FormControl fullWidth size="small">
            <InputLabel>Оператор</InputLabel>
            <Select
              value={condition.operator || 'равно'}
              label="Оператор"
              onChange={(e) => updateField('operator', e.target.value)}
            >
              {operators.map(op => <MenuItem key={op} value={op}>{op}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth size="small"
            label="Значение"
            value={condition.value !== undefined && condition.value !== null ? condition.value : ''}
            onChange={(e) => {
              const raw = e.target.value;
              let val = raw;
              // Автоконвертация чисел, если строка — чистое число
              if (raw.trim() !== '' && !isNaN(Number(raw))) {
                val = Number(raw);
              }
              updateField('value', val);
            }}
            placeholder="Значение для сравнения"
          />
        </Grid>

        {/* Динамические параметры */}
        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" gutterBottom>
            Параметры (дополнительно)
          </Typography>
          {Object.entries(condition.parameters || {}).map(([key, val]) => (
            <ParameterRow
              key={key}
              paramKey={key}
              paramValue={val}
              onChange={updateParamValue}
              onDelete={deleteParam}
              keyEditable={false}
            />
          ))}
          <Button size="small" startIcon={<AddIcon />} onClick={addParam}>
            Добавить параметр
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

// --- Редактор одного Действия ---
const ActionItem = ({
  action, index, onChange, onDelete,
  actionTypes, targetTypes
}) => {
  const updateField = (field, value) => {
    onChange(index, { ...action, [field]: value });
  };

  const updateParamValue = (oldKey, newKey, value) => {
    const params = { ...(action.parameters || {}) };
    if (newKey !== oldKey) {
      delete params[oldKey];
    }
    params[newKey] = value;
    updateField('parameters', params);
  };

  const deleteParam = (key) => {
    const params = { ...(action.parameters || {}) };
    delete params[key];
    updateField('parameters', params);
  };

  const addParam = () => {
    const key = window.prompt('Имя параметра (например: task, urgency):');
    if (!key) return;
    const trimmed = key.trim();
    if (!trimmed) return;
    const params = { ...(action.parameters || {}), [trimmed]: '' };
    updateField('parameters', params);
  };

  // Для отображения типа действия: ищем по "type" или "тип"
  const actionTypeValue =
    action.type !== undefined ? action.type
    : action['тип'] !== undefined ? action['тип']
    : '';

  const actionParams =
    action.parameters !== undefined ? action.parameters
    : action['параметры'] !== undefined ? action['параметры']
    : {};

  const targetTypeValue =
    action.target_type !== undefined ? action.target_type
    : action['цель'] !== undefined ? action['цель']
    : 'self';

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: 'background.default', borderLeft: '4px solid #4CAF50' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
        <Chip label={`Действие ${index + 1}`} size="small" color="success" variant="outlined" />
        <IconButton size="small" onClick={() => onDelete(index)} color="error">
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Тип действия</InputLabel>
            <Select
              value={actionTypeValue}
              label="Тип действия"
              onChange={(e) => updateField('type', e.target.value)}
            >
              {actionTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth size="small">
            <InputLabel>Цель (Target)</InputLabel>
            <Select
              value={targetTypeValue}
              label="Цель"
              onChange={(e) => updateField('target_type', e.target.value)}
            >
              {targetTypes.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', mb: 1 }}>
            Параметры действия (Контекст выполнения)
          </Typography>
          {Object.entries(actionParams).map(([key, val]) => (
            <ParameterRow
              key={key}
              paramKey={key}
              paramValue={val}
              onChange={updateParamValue}
              onDelete={deleteParam}
              keyEditable={true}
            />
          ))}
          <Button size="small" startIcon={<AddIcon />} color="secondary" onClick={addParam}>
            Добавить параметр
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

// --- Вспомогательное: генератор uid для условий/действий ---
let __uidCounter = 0;
const nextUid = () => {
  __uidCounter += 1;
  return `uid_${Date.now().toString(36)}_${__uidCounter}`;
};

// --- Основной компонент редактора ---
export default function RuleEditor({
  rule,
  onChange,
  onSave,
  onUpdateStatus,
  onDelete,
  ruleTypes,
  schema
}) {
  const [localRule, setLocalRule] = useState(rule || {});
  const [activeTab, setActiveTab] = useState('general');
  const [tagsInput, setTagsInput] = useState((rule?.tags || []).join(', '));

  // Наборы из schema (с бэкенда) с fallback на дефолты
  const conditionTypes = schema?.conditionTypes?.length
    ? schema.conditionTypes
    : DEFAULT_CONDITION_TYPES;
  const operators = schema?.operators?.length
    ? schema.operators
    : DEFAULT_OPERATORS;
  const actionTypes = schema?.actionTypes?.length
    ? schema.actionTypes
    : DEFAULT_ACTION_TYPES;
  const targetTypes = schema?.targetTypes?.length
    ? schema.targetTypes
    : DEFAULT_TARGET_TYPES;

  useEffect(() => {
    if (rule) {
      setLocalRule(rule);
      setTagsInput((rule.tags || []).join(', '));
    }
  }, [rule]);

  // Пробрасываем draft наружу при каждом изменении localRule
  const emitChange = (next) => {
    setLocalRule(next);
    if (typeof onChange === 'function') onChange(next);
  };

  const handleChange = (field, value) => {
    emitChange({ ...localRule, [field]: value });
  };

  // --- Условия ---

  const handleConditionChange = (index, newCondition) => {
    const newConditions = [...(localRule.conditions || [])];
    newConditions[index] = newCondition;
    handleChange('conditions', newConditions);
  };

  const addCondition = () => {
    const newConditions = [
      ...(localRule.conditions || []),
      {
        _uid: nextUid(),
        type: conditionTypes[0] || 'состояние_персонажа',
        operator: operators[0] || 'равно',
        field: '',
        value: '',
        parameters: {}
      }
    ];
    handleChange('conditions', newConditions);
  };

  const removeCondition = (index) => {
    const newConditions = (localRule.conditions || []).filter((_, i) => i !== index);
    handleChange('conditions', newConditions);
  };

  // --- Действия ---

  const handleActionChange = (index, newAction) => {
    const newActions = [...(localRule.actions || [])];
    newActions[index] = newAction;
    handleChange('actions', newActions);
  };

  const addAction = () => {
    const newActions = [
      ...(localRule.actions || []),
      {
        _uid: nextUid(),
        type: actionTypes[0] || 'назначить_задачу',
        target_type: targetTypes[0] || 'self',
        parameters: {}
      }
    ];
    handleChange('actions', newActions);
  };

  const removeAction = (index) => {
    const newActions = (localRule.actions || []).filter((_, i) => i !== index);
    handleChange('actions', newActions);
  };

  // --- Теги ---

  const commitTags = () => {
    const parsed = (tagsInput || '')
      .split(',')
      .map(s => s.trim())
      .filter(Boolean);
    handleChange('tags', parsed);
  };

  // --- Рендер ---

  const currentTypeConfig = useMemo(() => {
    return ruleTypes?.[localRule.type] || {
      label: localRule.type || 'Неизвестный',
      color: '#757575',
      icon: '❓'
    };
  }, [ruleTypes, localRule.type]);

  if (!localRule || !localRule.name) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'text.secondary' }}>
        <LogicIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
        <Typography>Выберите правило для редактирования</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Шапка */}
      <Paper sx={{ p: 2, mb: 2, bgcolor: 'background.paper' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              label="Название правила"
              value={localRule.name || ''}
              onChange={(e) => handleChange('name', e.target.value)}
              size="small"
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Autocomplete
              options={ruleTypes ? Object.keys(ruleTypes) : []}
              value={localRule.type || ''}
              onChange={(e, newVal) =>
                // Не даём очистить тип в пустую строку — иначе правило
                // при сохранении уедет в "универсальное".
                handleChange('type', newVal || localRule.type || 'универсальное')
              }
              getOptionLabel={(option) => {
                if (!option) return '';
                const cfg = ruleTypes?.[option];
                return cfg ? cfg.label : String(option);
              }}
              isOptionEqualToValue={(a, b) => a === b}
              renderInput={(params) => <TextField {...params} label="Тип правила" size="small" />}
              renderOption={(props, option) => (
                <li {...props} key={option}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <span>{ruleTypes?.[option]?.icon || '❓'}</span>
                    <span>{ruleTypes?.[option]?.label || option}</span>
                  </Box>
                </li>
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Приоритет"
              type="number"
              value={localRule.priority ?? 50}
              onChange={(e) => handleChange('priority', parseInt(e.target.value, 10) || 0)}
              size="small"
              InputProps={{ inputProps: { min: 1, max: 100 } }}
            />
          </Grid>
          <Grid item xs={12} md={2} sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Tooltip title={localRule.enabled ? 'Деактивировать' : 'Активировать'}>
              <IconButton
                color={localRule.enabled ? 'success' : 'default'}
                onClick={() => onUpdateStatus(localRule.enabled ? 'inactive' : 'active')}
              >
                {localRule.enabled ? <PlayIcon /> : <PauseIcon />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Сохранить">
              <IconButton color="primary" onClick={() => onSave(localRule)}>
                <SaveIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Удалить">
              <IconButton color="error" onClick={() => onDelete(localRule)}>
                <DeleteIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>

        <TextField
          fullWidth
          label="Описание"
          value={localRule.description || ''}
          onChange={(e) => handleChange('description', e.target.value)}
          size="small"
          sx={{ mt: 2 }}
          multiline
          rows={2}
        />
      </Paper>

      {/* Табы навигации */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Stack direction="row" spacing={1} sx={{ overflowX: 'auto' }}>
          <Button
            startIcon={<SettingsIcon />}
            onClick={() => setActiveTab('general')}
            variant={activeTab === 'general' ? 'contained' : 'text'}
          >
            Общие
          </Button>
          <Button
            startIcon={<LogicIcon />}
            onClick={() => setActiveTab('conditions')}
            variant={activeTab === 'conditions' ? 'contained' : 'text'}
          >
            Условия ({localRule.conditions?.length || 0})
          </Button>
          <Button
            startIcon={<ActionIcon />}
            onClick={() => setActiveTab('actions')}
            variant={activeTab === 'actions' ? 'contained' : 'text'}
          >
            Действия ({localRule.actions?.length || 0})
          </Button>
          <Button
            startIcon={<CopyIcon />}
            onClick={() => setActiveTab('json')}
            variant={activeTab === 'json' ? 'contained' : 'text'}
            sx={{ ml: 'auto' }}
          >
            JSON
          </Button>
        </Stack>
      </Box>

      {/* Контент вкладок */}
      <Box sx={{ flex: 1, overflow: 'auto', pb: 4 }}>

        {activeTab === 'general' && (
          <Card variant="outlined">
            <CardContent>
              <Typography variant="subtitle1" gutterBottom>Метаданные правила</Typography>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Источник"
                    value={localRule.source || 'manual'}
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth size="small"
                    label="Уверенность (0.0 - 1.0)"
                    type="number"
                    inputProps={{ step: 0.1, min: 0, max: 1 }}
                    value={localRule.confidence ?? 0.8}
                    onChange={(e) => handleChange('confidence', parseFloat(e.target.value) || 0)}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Теги (через запятую)"
                    value={tagsInput}
                    onChange={(e) => setTagsInput(e.target.value)}
                    onBlur={commitTags}
                    onKeyDown={(e) => { if (e.key === 'Enter') commitTags(); }}
                    helperText="Enter или уход из поля — применить"
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="ID (bit_id)"
                    value={localRule.bit_id || ''}
                    disabled
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth size="small"
                    label="Collection ID"
                    value={localRule.collection_id || ''}
                    disabled
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        )}

        {activeTab === 'conditions' && (
          <Box>
            <SectionTitle
              icon={<LogicIcon />}
              title="Условия срабатывания"
              action={
                <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={addCondition}>
                  Добавить условие
                </Button>
              }
            />
            {(localRule.conditions || []).length === 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Нет условий. Правило будет срабатывать всегда при наступлении события.
              </Alert>
            )}
            {(localRule.conditions || []).map((cond, idx) => (
              <ConditionItem
                key={cond._uid || cond.id || `cond_${idx}`}
                index={idx}
                condition={cond}
                onChange={handleConditionChange}
                onDelete={removeCondition}
                conditionTypes={conditionTypes}
                operators={operators}
              />
            ))}
          </Box>
        )}

        {activeTab === 'actions' && (
          <Box>
            <SectionTitle
              icon={<ActionIcon />}
              title="Действия при срабатывании"
              action={
                <Button
                  size="small" variant="contained" color="success"
                  startIcon={<AddIcon />} onClick={addAction}
                >
                  Добавить действие
                </Button>
              }
            />
            {(localRule.actions || []).length === 0 && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Нет действий. Правило ничего не сделает при срабатывании.
              </Alert>
            )}
            {(localRule.actions || []).map((act, idx) => (
              <ActionItem
                key={act._uid || act.id || `act_${idx}`}
                index={idx}
                action={act}
                onChange={handleActionChange}
                onDelete={removeAction}
                actionTypes={actionTypes}
                targetTypes={targetTypes}
              />
            ))}
          </Box>
        )}

        {activeTab === 'json' && (
          <Paper
            variant="outlined"
            sx={{
              p: 2, bgcolor: '#1e1e1e', color: '#d4d4d4',
              fontFamily: 'monospace', overflow: 'auto', maxHeight: '60vh'
            }}
          >
            <pre>{JSON.stringify(localRule, null, 2)}</pre>
          </Paper>
        )}
      </Box>
    </Box>
  );
}